// src/components/TimeClock.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from "@mui/material";
import Grid from '@mui/material/Grid';
import {
  Login as ClockInIcon,
  Logout as ClockOutIcon,
  Business as LocationIcon,
  LocationOn as LocationOnIcon,
} from "@mui/icons-material";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  orderBy,
  limit,
  Timestamp,
  doc,
} from "firebase/firestore";
import { db } from "../firebase";
import { tryResolveTenantId } from '../utils/tenant';
// findMatchingShift previously used for loose fallback matching; strict matching now enforced.
import findValidShiftsForPunch from '../utils/findValidShiftsForPunch';
import { updateShiftTimecard } from '../utils/shiftAttendance';
import { toDate } from '../utils/shiftDate';
import { formatTime as formatTimeString } from '../utils';
import { getBusinessDateKey } from '../utils/businessDateKey';

export default function TimeClock() {
  const [punchId, setPunchId] = useState("");
  const [employee, setEmployee] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [assignmentDocs, setAssignmentDocs] = useState([]); // normalized assignment records
  const [lastEntry, setLastEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showEmployeeInfo, setShowEmployeeInfo] = useState(false);

  // Shift selection flow (only when multiple valid scheduled shifts exist)
  const [shiftSelectionDialog, setShiftSelectionDialog] = useState({
    open: false,
    shifts: [],
    pendingEntry: null,
  });
  const [selectedShiftId, setSelectedShiftId] = useState("");
  const [recommendedShiftId, setRecommendedShiftId] = useState("");
  const [currentScheduledShift, setCurrentScheduledShift] = useState(null);
  const [lockedToSchedule, setLockedToSchedule] = useState(false);
  const [activeBreak, setActiveBreak] = useState(null);
  const [scheduledBreaks, setScheduledBreaks] = useState([]);

  const isClockedIn = useCallback(() => {
    if (!lastEntry) return false;
    return lastEntry.type === "clock_in" || lastEntry.type === "break_start" || lastEntry.type === "break_end";
  }, [lastEntry]);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Whenever selectedLocation or assignments change for the current employee,
  // reload roles so kiosk always reflects latest assignment docs.
  useEffect(() => {
    if (employee && selectedLocation) {
      loadRolesForLocation(employee, selectedLocation);
    } else {
      setAvailableRoles([]);
      setSelectedRole("");
    }
  // loadRolesForLocation is defined later in this component and isn't stable across renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee, selectedLocation, assignmentDocs]);

  // Auto-select location and role based on current scheduled shift
  useEffect(() => {
    async function preselectFromShift() {
      if (!employee || !assignmentDocs || assignmentDocs.length === 0) return;
      // Wait for roles to be loaded before pre-selecting
      if (availableRoles.length === 0) return;
      // Wait for roles to be loaded before pre-selecting
      if (availableRoles.length === 0) return;
      
      const tenantId = await tryResolveTenantId();
      if (!tenantId) return;

      // Find shift for current time
      const now = new Date();
      const punchForMatch = {
        employeeId: employee.id,
        timestamp: Timestamp.fromDate(now),
        locationId: null, // Don't filter by location yet
        roleId: null, // Don't filter by role yet
      };

      try {
        const validShifts = await findValidShiftsForPunch(tenantId, punchForMatch, { windowMinutes: 60 });
        if (validShifts.length > 0) {
          // Sort by closest start time to now
          const sortedShifts = validShifts.sort((a, b) => {
            const aStart = toDate(a.shiftDateKey || a.shiftDate);
            const bStart = toDate(b.shiftDateKey || b.shiftDate);
            const [aH, aM] = (a.shiftStartTime || '00:00').split(':').map(Number);
            const [bH, bM] = (b.shiftStartTime || '00:00').split(':').map(Number);
            aStart.setHours(aH, aM, 0, 0);
            bStart.setHours(bH, bM, 0, 0);
            return Math.abs(aStart - now) - Math.abs(bStart - now);
          });
          
          const currentShift = sortedShifts[0];
          const shiftLoc = getShiftLoc(currentShift);
          const shiftRole = getShiftRole(currentShift);
          
          // Save shift details for display
          setCurrentScheduledShift({
            id: currentShift.id,
            locationName: currentShift.locationName || 'Unknown Location',
            roleName: currentShift.roleName || 'Unknown Role',
            startTime: currentShift.shiftStartTime || currentShift.startTime || '00:00',
            endTime: currentShift.shiftEndTime || currentShift.endTime || '00:00',
            date: currentShift.shiftDateKey || currentShift.shiftDate
          });
          setLockedToSchedule(true);
          
          if (shiftLoc) setSelectedLocation(shiftLoc);
          if (shiftRole) setSelectedRole(shiftRole);
        }
      } catch (err) {
        console.warn('Error pre-selecting from shift:', err);
      }
    }

    preselectFromShift();
  }, [employee, assignmentDocs, availableRoles]);

  // Load active break for clocked-in employee
  useEffect(() => {
    if (!employee || !lastEntry || !isClockedIn()) {
      setActiveBreak(null);
      return;
    }

    const loadActiveBreak = async () => {
      try {
        const tenantId = await tryResolveTenantId();
        const timeEntriesRef = collection(db, `tenants/${tenantId}/timeEntries`);
        
        // Find any break_start without a matching break_end for this employee
        const breakStartQuery = query(
          timeEntriesRef,
          where('employeeId', '==', employee.id),
          where('type', '==', 'break_start'),
          orderBy('timestamp', 'desc'),
          limit(10)
        );
        
        const breakStartSnap = await getDocs(breakStartQuery);
        
        for (const startDoc of breakStartSnap.docs) {
          const startData = startDoc.data();
          
          // Check if there's a matching break_end
          const breakEndQuery = query(
            timeEntriesRef,
            where('employeeId', '==', employee.id),
            where('type', '==', 'break_end'),
            where('breakId', '==', startData.breakId || startDoc.id),
            where('timestamp', '>', startData.timestamp)
          );
          
          const breakEndSnap = await getDocs(breakEndQuery);
          
          if (breakEndSnap.empty) {
            // Found an active break
            setActiveBreak({
              id: startDoc.id,
              breakId: startData.breakId,
              startTime: startData.timestamp.toDate(),
              shiftId: startData.shiftId
            });
            return;
          }
        }
        
        setActiveBreak(null);
      } catch (err) {
        console.error('Error loading active break:', err);
      }
    };

    loadActiveBreak();
  }, [employee, lastEntry, isClockedIn]);

  // Load scheduled breaks from current shift
  useEffect(() => {
    if (!currentScheduledShift || !employee) {
      setScheduledBreaks([]);
      return;
    }

    const loadScheduledBreaks = async () => {
      try {
        const tenantId = await tryResolveTenantId();
        const shiftRef = doc(db, `tenants/${tenantId}/shifts/${currentScheduledShift.id}`);
        const shiftSnap = await getDoc(shiftRef);
        
        if (shiftSnap.exists()) {
          const shiftData = shiftSnap.data();
          setScheduledBreaks(shiftData.breaks || []);
        }
      } catch (err) {
        console.error('Error loading scheduled breaks:', err);
      }
    };

    loadScheduledBreaks();
  }, [currentScheduledShift, employee]);



  // Format time for display
  const formatTime = (date) => {
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString([], {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  

  // Handle number pad input
  const handleNumberClick = (number) => {
    if (punchId.length < 4) {
      setPunchId(prev => prev + number);
    }
  };

  // Clear punch ID

  // Break punch handlers
  const handleBreakStart = async () => {
    if (!employee || !isClockedIn()) {
      setMessage('❌ You must be clocked in to start a break');
      setMessageType('error');
      return;
    }

    if (activeBreak) {
      setMessage('❌ You already have an active break');
      setMessageType('error');
      return;
    }

    setLoading(true);
    try {
      const tenantId = await tryResolveTenantId();
      if (!tenantId) throw new Error('Could not resolve tenant ID');
      const now = new Date();
      const effectiveLocationId = lastEntry?.locationId || selectedLocation;
      const locationContext = await resolveLocationContext(
        tenantId,
        effectiveLocationId,
        lastEntry?.locationTimezone || 'America/Chicago'
      );

      // Determine which scheduled break this is (if any)
      let breakId = null;
      if (scheduledBreaks && scheduledBreaks.length > 0) {
        // Find the most appropriate scheduled break based on current time
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        
        // Find break closest to current time
        let closestBreak = null;
        let minDiff = Infinity;
        
        for (const scheduledBreak of scheduledBreaks) {
          if (!scheduledBreak.startTime) continue;
          
          const [hours, mins] = scheduledBreak.startTime.split(':');
          const breakMinutes = parseInt(hours) * 60 + parseInt(mins);
          const diff = Math.abs(currentMinutes - breakMinutes);
          
          if (diff < minDiff) {
            minDiff = diff;
            closestBreak = scheduledBreak;
          }
        }
        
        if (closestBreak) {
          breakId = closestBreak.id || closestBreak.name || 'break-1';
        }
      }

      const breakEntry = {
        employeeId: employee.id,
        employeeName: employee.name || 'Unknown',
        type: 'break_start',
        timestamp: locationContext.timestamp,
        date: locationContext.businessDateKey,
        businessDateKey: locationContext.businessDateKey,
        locationId: locationContext.locationId,
        locationTimezone: locationContext.locationTimezone,
        locationName: lastEntry?.locationName || locations.find(l => l.id === selectedLocation)?.name,
        roleId: lastEntry?.roleId || selectedRole,
        roleName: lastEntry?.roleName || availableRoles.find(r => r.id === selectedRole)?.name,
        shiftId: lastEntry?.shiftId || currentScheduledShift?.id || null,
        breakId: breakId,
        createdAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, `tenants/${tenantId}/timeEntries`), breakEntry);
      
      setActiveBreak({
        id: docRef.id,
        breakId: breakId,
        startTime: now,
        shiftId: breakEntry.shiftId
      });

      setMessage(`✅ Break started at ${formatTime(now)}`);
      setMessageType('success');
      
      // Reload time entries
      await getLastTimeEntry(employee.id);
    } catch (err) {
      console.error('Break start error:', err);
      setMessage(`❌ Error starting break: ${err.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleBreakEnd = async () => {
    if (!employee || !activeBreak) {
      setMessage('❌ No active break to end');
      setMessageType('error');
      return;
    }

    setLoading(true);
    try {
      const tenantId = await tryResolveTenantId();
      
      if (!tenantId) {
        throw new Error('Could not resolve tenant ID');
      }
      const now = new Date();
      const effectiveLocationId = lastEntry?.locationId || selectedLocation;
      const locationContext = await resolveLocationContext(
        tenantId,
        effectiveLocationId,
        lastEntry?.locationTimezone || 'America/Chicago'
      );

      const breakEndEntry = {
        employeeId: employee.id,
        employeeName: employee.name || 'Unknown',
        type: 'break_end',
        timestamp: locationContext.timestamp,
        date: locationContext.businessDateKey,
        businessDateKey: locationContext.businessDateKey,
        locationId: locationContext.locationId,
        locationTimezone: locationContext.locationTimezone,
        locationName: lastEntry?.locationName || locations.find(l => l.id === selectedLocation)?.name,
        roleId: lastEntry?.roleId || selectedRole,
        roleName: lastEntry?.roleName || availableRoles.find(r => r.id === selectedRole)?.name,
        shiftId: activeBreak.shiftId,
        breakId: activeBreak.breakId || activeBreak.id,
        breakStartId: activeBreak.id,
        createdAt: Timestamp.now()
      };

      await addDoc(collection(db, `tenants/${tenantId}/timeEntries`), breakEndEntry);
      
      const duration = Math.floor((now - activeBreak.startTime) / 60000);
      setActiveBreak(null);

      setMessage(`✅ Break ended at ${formatTime(now)} (Duration: ${duration} minutes)`);
      setMessageType('success');
      
      // Reload time entries
      await getLastTimeEntry(employee.id);
    } catch (err) {
      console.error('Break end error:', err);
      setMessage(`❌ Error ending break: ${err.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };




  const handleClear = () => {
    setPunchId("");
    setEmployee(null);
    setCurrentScheduledShift(null);
    setLockedToSchedule(false);
    setSelectedLocation("");
    setSelectedRole("");
    setAvailableRoles([]);
    setLastEntry(null);
    setMessage("");
    setShowEmployeeInfo(false);
  setAssignmentDocs([]);
  setShiftSelectionDialog({ open: false, shifts: [], pendingEntry: null });
  setSelectedShiftId("");
  };

  // Delete last digit
  const handleBackspace = () => {
    setPunchId(prev => prev.slice(0, -1));
  };

  // Find employee by punch ID
  const findEmployee = useCallback(async (id) => {
    if (id.length !== 4) return;

    setLoading(true);
    try {
      const tenantId = await tryResolveTenantId();
      if (!tenantId) {
        setMessage("Tenant not resolved.");
        setMessageType("error");
        setLoading(false);
        return;
      }

      const q = query(
        collection(db, `tenants/${tenantId}/employees`),
        where("employment.punchId", "==", id)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setMessage("Punch ID not found. Please check your number and try again.");
        setMessageType("error");
        setEmployee(null);
        setShowEmployeeInfo(false);
        return;
      }

      const employeeDoc = querySnapshot.docs[0];
      const employeeData = { id: employeeDoc.id, ...employeeDoc.data() };
      
      // Check if employee is active
      const status = employeeData.employment?.currentStatus;
      if (status && status !== "Active") {
        setMessage(`Cannot clock in. Employee status: ${status}`);
        setMessageType("error");
        setEmployee(null);
        setShowEmployeeInfo(false);
        return;
      }

      setEmployee(employeeData);
      setShowEmployeeInfo(true);
      
      // Load employee's assignments.
      // Supports both:
      // 1) legacy map: employee.assignments[locationId] = { departments: ... }
      // 2) current schema: subcollection employees/{id}/assignments where docs include locationId/departmentId/roleId.
      let normalizedAssignments = [];

      // (2) subcollection
      try {
        const aSnap = await getDocs(collection(db, `tenants/${tenantId}/employees`, employeeData.id, 'assignments'));
        normalizedAssignments = aSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (err) {
        // ignore; fallback to legacy
      }

      // (1) legacy map -> normalize
      if ((!normalizedAssignments || normalizedAssignments.length === 0) && employeeData.assignments) {
        try {
          for (const [locationId, locAssignments] of Object.entries(employeeData.assignments)) {
            if (!locAssignments?.departments) continue;
            for (const [departmentId, deptData] of Object.entries(locAssignments.departments)) {
              if (!deptData?.roles) continue;
              const roleIds = Array.isArray(deptData.roles)
                ? deptData.roles
                : Object.keys(deptData.roles).filter(roleId => deptData.roles[roleId]?.assigned);
              for (const roleId of roleIds) {
                const isPrimary = !Array.isArray(deptData.roles) && deptData.roles?.[roleId]?.isPrimary;
                normalizedAssignments.push({
                  locationId,
                  departmentId,
                  roleId,
                  isPrimary: !!isPrimary,
                });
              }
            }
          }
        } catch (err) {
          // ignore
        }
      }

      setAssignmentDocs(normalizedAssignments);

      // Load employee's locations from normalized assignment records
      const employeeLocations = [];
      const locationIds = Array.from(new Set((normalizedAssignments || []).map(a => a.locationId).filter(Boolean)));
      for (const locationId of locationIds) {
        try {
          const locationSnap = await getDoc(doc(db, `tenants/${tenantId}/locations`, locationId));
          if (locationSnap.exists()) {
            const locationData = locationSnap.data();
            employeeLocations.push({ id: locationId, name: locationData.name });
          }
        } catch (err) {
          // ignore
        }
      }
      
      setLocations(employeeLocations);
      
      // Auto-select location if only one
      if (employeeLocations.length === 1) {
        setSelectedLocation(employeeLocations[0].id);
        await loadRolesForLocation(employeeData, employeeLocations[0].id);
      }

      // Get last time entry
      await getLastTimeEntry(employeeData.id);
      
      setMessage("");
    } catch (error) {
      console.error("Error finding employee:", error);
      setMessage("Error looking up employee. Please try again.");
      setMessageType("error");
    }
    setLoading(false);
    // loadRolesForLocation is defined later in this component and isn't stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load roles for selected location
  const loadRolesForLocation = async (emp, locationId) => {
    try {
  const tenantId = await tryResolveTenantId();
  if (!tenantId) return;
      const roles = [];
      // Prefer normalized assignment docs (subcollection schema)
      if (assignmentDocs && assignmentDocs.length > 0) {
  // Debug logs removed (kept the logic only)
        // Your assignment docs (per screenshot) look like:
        // { locationId, locationName, departmentId, departmentName, roleId, roleName, appearsOnSchedule, assigned, isPrimary }
        const getLocId = (a) => a.locationId || a.locationID || a.location || null;
        const getRoleId = (a) => a.roleId || a.roleID || a.role || (a.role && a.role.id) || null;

  const roleIds = assignmentDocs
          .filter(a => getLocId(a) === locationId && (a.assigned !== false)) // treat missing assigned as true
          .map(a => getRoleId(a))
          .filter(Boolean);

        const uniqueRoleIds = Array.from(new Set(roleIds));
        for (const roleId of uniqueRoleIds) {
          try {
            const roleSnap = await getDoc(doc(db, `tenants/${tenantId}/roles`, roleId));
            if (roleSnap.exists()) {
              const roleData = roleSnap.data();
              const matchingAssignments = assignmentDocs.filter(a => getLocId(a) === locationId && getRoleId(a) === roleId);
              const anyAssignment = matchingAssignments[0] || {};
              const isPrimary = !!matchingAssignments.find(a => a.isPrimary || a.primary || a.is_primary);
              roles.push({
                id: roleId,
                name: roleData.name || anyAssignment.roleName || "(role)",
                color: roleData.color || "#888",
                isPrimary,
                departmentId: anyAssignment.departmentId || anyAssignment.departmentID || roleData.departmentId || null,
              });
            }
          } catch (err) {
            // ignore
          }
        }

        setAvailableRoles(roles);

        // Auto-select primary role if exists
        const primaryRole = roles.find(r => r.isPrimary);
        if (primaryRole) {
          setSelectedRole(primaryRole.id);
        } else if (roles.length === 1) {
          setSelectedRole(roles[0].id);
        }
        return;
      }

      // Legacy map schema fallback
      const assignments = emp.assignments?.[locationId];
      
      if (assignments?.departments) {
        for (const [deptId, deptData] of Object.entries(assignments.departments)) {
          if (deptData.roles) {
            let roleIds = [];
            
            if (Array.isArray(deptData.roles)) {
              // Legacy format
              roleIds = deptData.roles;
            } else {
              // New format - get assigned roles
              roleIds = Object.keys(deptData.roles).filter(roleId => 
                deptData.roles[roleId]?.assigned
              );
            }
            
            // Get role details
            for (const roleId of roleIds) {
              try {
                const roleSnap = await getDoc(doc(db, `tenants/${tenantId}/roles`, roleId));
                if (roleSnap.exists()) {
                  const roleData = roleSnap.data();
                  const isPrimary = !Array.isArray(deptData.roles) && deptData.roles[roleId]?.isPrimary;
                  roles.push({
                    id: roleId,
                    name: roleData.name,
                    color: roleData.color || "#888",
                    isPrimary,
                    departmentId: deptId
                  });
                }
              } catch (err) {
                // ignore
              }
            }
          }
        }
      }
      
      setAvailableRoles(roles);
      
      // Auto-select primary role if exists
      const primaryRole = roles.find(r => r.isPrimary);
      if (primaryRole) {
        setSelectedRole(primaryRole.id);
      } else if (roles.length === 1) {
        setSelectedRole(roles[0].id);
      }
      
    } catch (error) {
      console.error("Error loading roles:", error);
    }
  };

  // Get last time entry for employee
  const getLastTimeEntry = async (employeeId) => {
    let tenantId = null;
    try {
      tenantId = await tryResolveTenantId();
      if (!tenantId) return setLastEntry(null);

      const q = query(
        collection(db, `tenants/${tenantId}/timeEntries`),
        where("employeeId", "==", employeeId),
        orderBy("timestamp", "desc"),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Prefer the most recent non-voided entry
        const entries = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(e => e.status !== 'voided');
        if (entries.length > 0) {
          setLastEntry(entries[0]);
        } else {
          setLastEntry(null);
        }
      } else {
        setLastEntry(null);
      }
    } catch (error) {
      console.error("Error getting last time entry:", error);
      // If there's an error with the query (like missing index), try a simpler approach
      try {
        if (!tenantId) tenantId = await tryResolveTenantId();
        if (!tenantId) return setLastEntry(null);

        const simpleQuery = query(
          collection(db, `tenants/${tenantId}/timeEntries`),
          where("employeeId", "==", employeeId)
        );
        const simpleSnapshot = await getDocs(simpleQuery);
        
        if (!simpleSnapshot.empty) {
          // Manually sort by timestamp to get the most recent
          const entries = simpleSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(e => e.status !== 'voided');
          const sortedEntries = entries.sort((a, b) => {
            const timeA = a.timestamp?.toDate?.() || new Date(a.timestamp);
            const timeB = b.timestamp?.toDate?.() || new Date(b.timestamp);
            return timeB - timeA;
          });
          const lastEntryData = sortedEntries[0];
          setLastEntry(lastEntryData);
        } else {
          setLastEntry(null);
        }
      } catch (simpleError) {
        console.error("Error with simple query:", simpleError);
        setLastEntry(null);
      }
    }
  };

  // Helper: map display/GMT timezone labels to IANA where possible
  const mapDisplayToIana = useCallback((s) => {
    if (!s) return null;
    const low = String(s).toLowerCase();
    if (low.includes('/')) return s;
    if (low.includes('central')) return 'America/Chicago';
    if (low.includes('eastern')) return 'America/New_York';
    if (low.includes('pacific')) return 'America/Los_Angeles';
    if (low.includes('mountain')) return 'America/Denver';
    if (low.includes('alaska')) return 'America/Anchorage';
    if (low.includes('hawaii')) return 'Pacific/Honolulu';
    const g = low.match(/gmt\s*([+-]?\d{1,2}):?(\d{2})?/);
    if (g) {
      const off = g[1];
      if (off === '-6' || off === '-06') return 'America/Chicago';
      if (off === '-5' || off === '-05') return 'America/New_York';
      if (off === '-8' || off === '-08') return 'America/Los_Angeles';
      if (off === '-7' || off === '-07') return 'America/Denver';
      if (off === '0' || off === '+0' || off === '+00') return 'Etc/UTC';
    }
    return null;
  }, []);

  const resolveLocationIana = useCallback((loc) => {
    if (!loc) return null;
    if (loc.ianaTimezone) return loc.ianaTimezone;
    if (loc.timezone && String(loc.timezone).includes('/')) return loc.timezone;
    const candidate = mapDisplayToIana(loc.timezone || loc.locationTimezone || loc.name || '');
    return candidate;
  }, [mapDisplayToIana]);

  const getShiftLoc = (shift) => shift?.locationId || shift?.locationID || shift?.location || null;
  const getShiftRole = (shift) => shift?.roleId || shift?.roleID || shift?.role || null;

  const resolveLocationContext = useCallback(async (tenantId, locationId, fallbackTimezone = 'America/Chicago') => {
    let locationData = locations.find((loc) => loc.id === locationId) || null;

    if (!locationData && tenantId && locationId) {
      const locationSnap = await getDoc(doc(db, `tenants/${tenantId}/locations`, locationId));
      if (locationSnap.exists()) {
        locationData = locationSnap.data();
      }
    }

    const locationTimezone =
      resolveLocationIana(locationData) ||
      fallbackTimezone ||
      'America/Chicago';
    const timestamp = Timestamp.now();

    return {
      locationId: locationId || null,
      locationData,
      locationTimezone,
      businessDateKey: getBusinessDateKey(
        locationData || { timezone: locationTimezone },
        timestamp.toDate()
      ),
      timestamp,
    };
  }, [locations, resolveLocationIana]);

  // Handle clock in/out
  const handlePunch = async () => {
    if (!employee || !selectedLocation) {
      setMessage("Please select a location.");
      setMessageType("error");
      return;
    }

    if (!selectedRole) {
      setMessage("Please select a role.");
      setMessageType("error");
      return;
    }

  setLoading(true);
    
    // Refresh last entry before determining clock in/out action
    await getLastTimeEntry(employee.id);
    
  const clockingIn = !isClockedIn();
    
    try {
      const tenantId = await tryResolveTenantId();
      if (!tenantId) throw new Error('Tenant not resolved');
      const locationContext = await resolveLocationContext(tenantId, selectedLocation);

  const timeEntry = {
        employeeId: employee.id,
        employeeName: `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || employee.name || 'Unknown',
  locationId: selectedLocation,
  locationTimezone: locationContext.locationTimezone,
        roleId: selectedRole,
        type: clockingIn ? "clock_in" : "clock_out",
        timestamp: locationContext.timestamp,
  date: locationContext.businessDateKey,
  businessDateKey: locationContext.businessDateKey,
  createdAt: Timestamp.now(),
  // include auth UID when present on the employee doc
  authUid: employee.authUid || employee.uid || null,
      };

    // Shift matching rules:
    // - If exactly 1 valid scheduled shift exists => auto attach
    // - If multiple => user must pick the correct shift before saving
    // - If none => allow manual entry but flag for review
    const punchForMatch = {
      employeeId: employee.id,
      timestamp: timeEntry.timestamp,
      date: locationContext.businessDateKey,
      locationId: selectedLocation,
      roleId: selectedRole,
    };

    let validShifts = [];
    try {
      validShifts = await findValidShiftsForPunch(tenantId, punchForMatch, { windowMinutes: 60 });
    } catch (err) {
      console.warn('Error finding valid shifts for punch', err);
      validShifts = [];
    }

    // Enforce strict match on both scheduled location and role.
    const strictMatches = validShifts.filter((s) => {
      const shiftLoc = getShiftLoc(s);
      const shiftRole = getShiftRole(s);

      const locMatches = !shiftLoc || !selectedLocation || shiftLoc === selectedLocation;
      const roleMatches = !shiftRole || !selectedRole || shiftRole === selectedRole;

      // Require both to match when present
      return locMatches && roleMatches;
    });

    if (strictMatches.length === 1) {
      timeEntry.shiftId = strictMatches[0].id;
      timeEntry.shiftMatchType = 'auto';
      timeEntry.requiresReview = false;
    } else if (strictMatches.length > 1) {
      // Pause and let user pick between strictly matching shifts.
      // Recommend the shift whose start time is closest to "now".
      // Important: compute the shift start using the same local-date parsing as the matching
      // util, otherwise timezone differences can cause the wrong shift to be highlighted.
      const now = timeEntry.timestamp?.toDate?.() || new Date();
      const toStartMs = (s) => {
        const shiftDate = s.shiftDateKey
          ? toDate(s.shiftDateKey)
          : (s.shiftDate ? toDate(s.shiftDate) : new Date(now));
        const [hh, mm] = String(s.shiftStartTime || s.startTime || '00:00')
          .split(':')
          .map((n) => Number(n));
        const d = new Date(shiftDate);
        d.setHours(Number.isFinite(hh) ? hh : 0, Number.isFinite(mm) ? mm : 0, 0, 0);
        return d.getTime();
      };

      let bestId = strictMatches[0]?.id || '';
      let bestDist = Infinity;
      for (const s of strictMatches) {
        const startMs = toStartMs(s);
        if (!Number.isFinite(startMs)) continue;
        const dist = Math.abs(startMs - now.getTime());
        if (dist < bestDist) {
          bestDist = dist;
          bestId = s.id;
        }
      }

      setShiftSelectionDialog({ open: true, shifts: strictMatches, pendingEntry: timeEntry });
      setRecommendedShiftId(bestId);
      setSelectedShiftId(bestId);
      setLoading(false);
      return;
    } else {
      // Either no shifts in window or none matching both location and role.
      // If there ARE scheduled shifts in the window but none match the selected location/role,
      // require explicit confirmation before allowing an unscheduled punch.
      if (validShifts.length > 0) {
        const ok = window.confirm(
          'You are not scheduled for this location and/or role right now. ' +
            'Clocking in/out will require manager approval. Continue?'
        );
        if (!ok) {
          setLoading(false);
          return;
        }
        timeEntry.unscheduledReason = 'location_or_role_mismatch';
      }

      timeEntry.shiftId = null;
      timeEntry.shiftMatchType = 'manual';
      timeEntry.requiresReview = true;
    }

  // Backward compatibility: previously we used a best-match scoring helper.
  // With strict location+role enforcement, we no longer auto-attach a shift
  // when strict matching fails; entries remain manual/requiresReview instead.

    await addDoc(collection(db, `tenants/${tenantId}/timeEntries`), timeEntry);
    if (timeEntry.shiftId) {
      await updateShiftTimecard(tenantId, timeEntry.shiftId);
    }
      
      setMessage(clockingIn 
        ? `✅ Clocked in successfully at ${formatTime(new Date())}`
        : `✅ Clocked out successfully at ${formatTime(new Date())}`
      );
      setMessageType("success");
      
      // Refresh last entry after successful punch
      await getLastTimeEntry(employee.id);
      
      // Auto-clear after success
      setTimeout(() => {
        handleClear();
      }, 3000);
      
    } catch (error) {
      console.error("Error recording time entry:", error);
      setMessage("Error recording time entry. Please try again.");
      setMessageType("error");
    }
    setLoading(false);
  };

  const finalizePunchWithSelectedShift = async () => {
    if (!shiftSelectionDialog?.pendingEntry) return;
    if (!selectedShiftId) {
      setMessage('Please select the correct shift.');
      setMessageType('error');
      return;
    }

    setLoading(true);
    try {
      const tenantId = await tryResolveTenantId();
      if (!tenantId) throw new Error('Tenant not resolved');

      const chosenShift = shiftSelectionDialog.shifts.find(s => s.id === selectedShiftId) || null;
      const shiftLoc = getShiftLoc(chosenShift);
      const shiftRole = getShiftRole(chosenShift);
      const locMatches = !shiftLoc || !selectedLocation || shiftLoc === selectedLocation;
      const roleMatches = !shiftRole || !selectedRole || shiftRole === selectedRole;

      // If the selected shift doesn't match the selected location/role, require confirmation and save as manual/review.
      if (!locMatches || !roleMatches) {
        const ok = window.confirm(
          'This clock-in/out does not match the scheduled location and/or role. ' +
            'It will require manager approval. Continue?'
        );
        if (!ok) {
          setLoading(false);
          return;
        }

        const timeEntry = {
          ...shiftSelectionDialog.pendingEntry,
          shiftId: null,
          shiftMatchType: 'manual',
          requiresReview: true,
          unscheduledReason: 'location_or_role_mismatch',
        };

        await addDoc(collection(db, `tenants/${tenantId}/timeEntries`), timeEntry);
        setShiftSelectionDialog({ open: false, shifts: [], pendingEntry: null });
        setSelectedShiftId("");
        setRecommendedShiftId("");

        setMessage(timeEntry.type === 'clock_in'
          ? `✅ Clock-in recorded (pending manager approval) at ${formatTime(new Date())}`
          : `✅ Clock-out recorded (pending manager approval) at ${formatTime(new Date())}`
        );
        setMessageType('warning');

        await getLastTimeEntry(employee.id);
        setTimeout(() => {
          handleClear();
        }, 3000);
        setLoading(false);
        return;
      }

      const timeEntry = {
        ...shiftSelectionDialog.pendingEntry,
        shiftId: selectedShiftId,
        shiftMatchType: 'user_selected',
        requiresReview: false,
      };

      await addDoc(collection(db, `tenants/${tenantId}/timeEntries`), timeEntry);
      await updateShiftTimecard(tenantId, selectedShiftId);

      setShiftSelectionDialog({ open: false, shifts: [], pendingEntry: null });
      setSelectedShiftId("");
  setRecommendedShiftId("");

      setMessage(timeEntry.type === 'clock_in'
        ? `✅ Clocked in successfully at ${formatTime(new Date())}`
        : `✅ Clocked out successfully at ${formatTime(new Date())}`
      );
      setMessageType('success');

      await getLastTimeEntry(employee.id);

      setTimeout(() => {
        handleClear();
      }, 3000);
    } catch (err) {
      console.error('Error recording selected-shift time entry:', err);
      setMessage('Error recording time entry. Please try again.');
      setMessageType('error');
    }
    setLoading(false);
  };

  // Auto-find employee when punch ID is complete
  useEffect(() => {
    if (punchId.length === 4) {
      findEmployee(punchId);
    }
  }, [punchId, findEmployee]);

  

  return (
    <Container maxWidth="md" sx={{ mt: 2, mb: 2 }}>
      {/* Header */}
  <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/LogoNew.svg`}
          alt="Company logo"
          sx={{ height: { xs: 80, sm: 120, md: 140 }, mb: 2, width: 'auto', objectFit: 'contain' }}
        />
  {/* Title removed per design request */}
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
          {formatDate(currentTime)}
        </Typography>
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: '2rem', sm: '2.75rem', md: '3rem' },
            fontWeight: 'bold',
            color: 'primary.main',
            mb: 1
          }}
        >
          {formatTime(currentTime)}
        </Typography>
      </Box>

      {/* Main Card */}
      <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 3 }}>
        {!showEmployeeInfo ? (
          /* Punch ID Entry */
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              Enter your Punch ID
            </Typography>
            
            {/* Punch ID Display */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 2, 
              mb: 4,
              '& > div': {
                width: 60,
                height: 60,
                border: '2px solid',
                borderColor: 'primary.main',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                bgcolor: 'background.paper'
              }
            }}>
              {[0, 1, 2, 3].map(index => (
                <Box key={index}>
                  {punchId[index] || ''}
                </Box>
              ))}
            </Box>

            {/* Number Pad */}
            <Grid container spacing={2} sx={{ maxWidth: 300, mx: 'auto', mb: 3 }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                <Grid item xs={4} key={num}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => handleNumberClick(num.toString())}
                    disabled={loading}
                    sx={{ 
                      width: '100%', 
                      height: 60, 
                      fontSize: '1.5rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {num}
                  </Button>
                </Grid>
              ))}
              <Grid item xs={4}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleClear}
                  disabled={loading}
                  sx={{ 
                    width: '100%', 
                    height: 60, 
                    fontSize: '1rem',
                    color: 'error.main',
                    borderColor: 'error.main'
                  }}
                >
                  Clear
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => handleNumberClick('0')}
                  disabled={loading}
                  sx={{ 
                    width: '100%', 
                    height: 60, 
                    fontSize: '1.5rem',
                    fontWeight: 'bold'
                  }}
                >
                  0
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleBackspace}
                  disabled={loading}
                  sx={{ 
                    width: '100%', 
                    height: 60, 
                    fontSize: '1rem'
                  }}
                >
                  ⌫
                </Button>
              </Grid>
            </Grid>
          </Box>
        ) : (
          /* Employee Info & Clock In/Out */
          <Box>
            {/* Employee Info */}
            <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Welcome, {employee.firstName} {employee.lastName}
                </Typography>
                
                {/* Current Status */}
                {lastEntry && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Chip 
                      icon={isClockedIn() ? <ClockInIcon /> : <ClockOutIcon />}
                      label={isClockedIn() ? "Currently Clocked In" : "Currently Clocked Out"}
                      color={isClockedIn() ? "success" : "default"}
                      variant={isClockedIn() ? "filled" : "outlined"}
                    />
                    {isClockedIn() && (
                      <Typography variant="body2" color="text.secondary">
                        Since: {new Date(lastEntry.timestamp.toDate()).toLocaleTimeString()}
                      </Typography>
                    )}
                  </Box>
                )}


              {/* Scheduled Shift - Compact Card */}
              {currentScheduledShift && (
                <Box
                  sx={{
                    mt: 1,
                    mb: 2,
                    p: 1.5,
                    borderLeft: '4px solid',
                    borderLeftColor: 'primary.main',
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                      Your shift today
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      color: (() => {
                        const now = new Date();
                        const [startHour, startMin] = currentScheduledShift.startTime.split(':');
                        const shiftStart = new Date();
                        shiftStart.setHours(parseInt(startHour), parseInt(startMin), 0, 0);
                        const diffMs = shiftStart - now;
                        const diffMins = Math.floor(diffMs / 60000);
                        
                        if (diffMins < -15) return 'error.main'; // Started more than 15 min ago
                        if (diffMins < 0) return 'success.main'; // Started recently
                        if (diffMins <= 15) return 'warning.main'; // Starting soon
                        return 'text.secondary';
                      })(),
                      fontSize: '0.7rem',
                      fontWeight: 500
                    }}>
                      {(() => {
                        const now = new Date();
                        const [startHour, startMin] = currentScheduledShift.startTime.split(':');
                        const shiftStart = new Date();
                        shiftStart.setHours(parseInt(startHour), parseInt(startMin), 0, 0);
                        const diffMs = shiftStart - now;
                        const diffMins = Math.floor(diffMs / 60000);
                        
                        if (diffMins < 0) {
                          const elapsed = Math.abs(diffMins);
                          if (elapsed < 60) return `Started ${elapsed}m ago`;
                          const hours = Math.floor(elapsed / 60);
                          const mins = elapsed % 60;
                          return mins > 0 ? `Started ${hours}h ${mins}m ago` : `Started ${hours}h ago`;
                        } else {
                          if (diffMins < 60) return `Starts in ${diffMins}m`;
                          const hours = Math.floor(diffMins / 60);
                          const mins = diffMins % 60;
                          return mins > 0 ? `Starts in ${hours}h ${mins}m` : `Starts in ${hours}h`;
                        }
                      })()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOnIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" fontWeight="medium">
                        {currentScheduledShift.locationName}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">•</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {currentScheduledShift.roleName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">•</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {formatTimeString(currentScheduledShift.startTime)} - {formatTimeString(currentScheduledShift.endTime)}
                    </Typography>
                  </Box>
                </Box>
              )}


              </CardContent>
            </Card>

            {/* Location Selection */}
            {showEmployeeInfo && locations.length === 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No locations are assigned to your profile. Please contact a manager.
              </Alert>
            )}
            
            {lockedToSchedule && currentScheduledShift ? (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Location
                  </Typography>
                  <Button 
                    size="small" 
                    onClick={() => setLockedToSchedule(false)}
                    disabled={isClockedIn()}
                    sx={{ minWidth: 'auto', fontSize: '0.75rem', py: 0.25 }}
                  >
                    Change
                  </Button>
                </Box>
                <Box sx={{ 
                  p: 1.25, 
                  border: '1px solid', 
                  borderColor: 'divider', 
                  borderRadius: 1,
                  bgcolor: 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <LocationIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                  <Typography variant="body2" fontWeight="medium">
                    {currentScheduledShift.locationName}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontSize: '0.7rem' }}>
                  Locked to scheduled shift
                </Typography>
              </Box>
            ) : (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Location</InputLabel>
                <Select
                  value={selectedLocation}
                  label="Select Location"
                  onChange={(e) => {
                    setSelectedLocation(e.target.value);
                    loadRolesForLocation(employee, e.target.value);
                  }}
                  startAdornment={<LocationIcon sx={{ mr: 1, color: 'action.active' }} />}
                  disabled={locations.length === 0}
                >
                  {locations.map(location => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Role Selection */}
            {selectedLocation && (
              <>
              {availableRoles.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No roles are assigned for this location. Please contact a manager.
                </Alert>
              )}
              
              {lockedToSchedule && currentScheduledShift ? (
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Role
                    </Typography>
                    <Button 
                      size="small" 
                      onClick={() => setLockedToSchedule(false)}
                      disabled={isClockedIn()}
                      sx={{ minWidth: 'auto', fontSize: '0.75rem', py: 0.25 }}
                    >
                      Change
                    </Button>
                  </Box>
                  <Box sx={{ 
                    p: 1.25, 
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: availableRoles.find(r => r.name === currentScheduledShift.roleName)?.color || 'primary.main'
                      }}
                    />
                    <Typography variant="body2" fontWeight="medium">
                      {currentScheduledShift.roleName}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontSize: '0.7rem' }}>
                    Locked to scheduled shift
                  </Typography>
                </Box>
              ) : (
                <FormControl fullWidth sx={{ mb: 2.5 }}>
                  <InputLabel>Select Role</InputLabel>
                  <Select
                    value={selectedRole}
                    label="Select Role"
                    onChange={(e) => setSelectedRole(e.target.value)}
                    disabled={availableRoles.length === 0}
                  >
                    {availableRoles.map(role => (
                      <MenuItem key={role.id} value={role.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: role.color
                            }}
                          />
                          {role.name}
                          {role.isPrimary && (
                            <Chip label="Primary" size="small" color="primary" sx={{ ml: 1 }} />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              </>
            )}

            {/* Break Button */}
            {isClockedIn() && (
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Button
                  variant={activeBreak ? "contained" : "outlined"}
                  color={activeBreak ? "error" : "warning"}
                  onClick={activeBreak ? handleBreakEnd : handleBreakStart}
                  disabled={loading}
                  startIcon={activeBreak ? <ClockOutIcon /> : <ClockInIcon />}
                  sx={{ 
                    minWidth: 200,
                    py: 1.25,
                    fontWeight: 600,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2
                    }
                  }}
                >
                  {activeBreak ? 'END BREAK' : 'START BREAK'}
                </Button>
                {activeBreak && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Break started at {formatTime(activeBreak.startTime)}
                  </Typography>
                )}
              </Box>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handlePunch}
                disabled={loading || !selectedLocation || !selectedRole}
                startIcon={isClockedIn() ? <ClockOutIcon /> : <ClockInIcon />}
                color={isClockedIn() ? "error" : "success"}
                sx={{ 
                  px: 4, 
                  py: 2, 
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  minWidth: 200
                }}
              >
                {loading ? "Processing..." : (isClockedIn() ? "Clock Out" : "Clock In")}
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                onClick={handleClear}
                disabled={loading}
                sx={{ px: 4, py: 2 }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {/* Messages */}
        {message && (
          <Alert 
            severity={messageType} 
            sx={{ mt: 3 }}
            onClose={() => setMessage("")}
          >
            {message}
          </Alert>
        )}

        {/* Shift selection overlay (simple, kiosk-safe) */}
        {shiftSelectionDialog.open && (
          <Box sx={{ mt: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Multiple scheduled shifts match your punch. Please select the correct shift.
            </Alert>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Shift</InputLabel>
              <Select
                value={selectedShiftId}
                label="Select Shift"
                onChange={(e) => setSelectedShiftId(e.target.value)}
              >
                {shiftSelectionDialog.shifts.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {`${s.shiftStartTime || s.startTime || ''}-${s.shiftEndTime || s.endTime || ''}`}
                    {s.roleName ? ` • ${s.roleName}` : ''}
                    {recommendedShiftId && s.id === recommendedShiftId ? ' • Recommended' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={finalizePunchWithSelectedShift}
                disabled={loading || !selectedShiftId}
              >
                Confirm Shift
              </Button>
              <Button
                variant="outlined"
                onClick={handleClear}
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
}
